import type { Scholarship } from '../../../../types/scholarships';
import { isRecruitingBoard } from './boardPurpose';

/**
 * Which summary cards a roster can show above the board, and when each one is available.
 *
 * The keys mirror `SummaryCards` in the API (`DTOs/ScholarshipResponse.cs`) — change both
 * together. An unlisted key stored on a roster is simply not rendered, which is the read
 * contract the API documents: a key this build does not recognise must never make a roster
 * unreadable.
 */
export const SUMMARY_CARD_KEYS = [
  'positionNeeds',
  'progress',
  'classSummary',
  'rosterSpots',
  'allocation',
  'nil',
  'revShare',
  'model',
] as const;

export type SummaryCardKey = (typeof SUMMARY_CARD_KEYS)[number];

interface SummaryCardMeta {
  key: SummaryCardKey;
  /** Shown in the picker. Card headings live in the card markup. */
  label: string;
  /**
   * Whether this roster can show the card at all, independent of whether the coach selected
   * it. This is a RENDER-TIME filter and never prunes what is stored: a coach who picks the
   * NIL card, switches NIL tracking off, then switches it back on finds their card still
   * selected. Collapsing the two would silently discard a choice.
   */
  availableWhen: (s: Scholarship) => boolean;
}

const ALWAYS = () => true;

export const SUMMARY_CARDS: SummaryCardMeta[] = [
  { key: 'positionNeeds', label: 'Position needs', availableWhen: ALWAYS },
  { key: 'progress', label: 'Recruiting progress', availableWhen: ALWAYS },
  { key: 'classSummary', label: 'Class summary', availableWhen: ALWAYS },
  // Not on a recruiting board: it has no roster spots setting (Configure board hides it), so the
  // card would report against a limit the coach cannot see or change. The stored choice is kept.
  { key: 'rosterSpots', label: 'Roster spots', availableWhen: (s) => !isRecruitingBoard(s) },
  { key: 'allocation', label: 'Scholarship allocation', availableWhen: ALWAYS },
  { key: 'nil', label: 'NIL budget', availableWhen: (s) => s.nilTrackingEnabled },
  { key: 'revShare', label: 'Revenue share budget', availableWhen: (s) => s.revShareTrackingEnabled },
  {
    key: 'model',
    label: 'Scholarship model',
    // Preserves the pre-registry rule: the model card was a FALLBACK that appeared only when
    // neither money card did, keeping the band to four cards. The active model stays visible
    // either way via the allocation card's suffix.
    availableWhen: (s) => !s.nilTrackingEnabled && !s.revShareTrackingEnabled,
  },
];

const CARD_ORDER = new Map<string, number>(
  SUMMARY_CARDS.map((c, i) => [c.key, i]),
);

const isKnownKey = (key: string): key is SummaryCardKey =>
  CARD_ORDER.has(key);

/**
 * The cards to actually render, from what the roster has stored.
 *
 * Drops keys this build does not know and keys the roster cannot show, then sorts into
 * registry order. Rendering in registry order rather than stored order is deliberate for
 * now: without it, toggling a card off and back on would move it to the end of the band,
 * which reads as a bug. If per-roster reordering is ever built, that is the line to relax.
 */
export const resolveVisibleCards = (
  stored: string[],
  scholarship: Scholarship,
): SummaryCardKey[] => {
  const selected = new Set(stored.filter(isKnownKey));
  return SUMMARY_CARDS
    .filter((c) => selected.has(c.key) && c.availableWhen(scholarship))
    .map((c) => c.key);
};

/**
 * The cards the picker offers — every card this roster *could* show, whether or not it is
 * currently selected. A card the roster cannot show is omitted rather than disabled: a
 * disabled NIL toggle invites the coach to look for a way to enable it here, when the
 * control that governs it is NIL tracking over in Configure roster.
 */
export const availableCards = (scholarship: Scholarship): SummaryCardMeta[] =>
  SUMMARY_CARDS.filter((c) => c.availableWhen(scholarship));

/**
 * What a toggle writes back. Takes the roster's stored keys so a selection this build does
 * not recognise survives the round trip — dropping it here is how a coach on an older tab
 * would silently clear a card a newer build had set.
 */
export const toggleCard = (
  stored: string[],
  key: SummaryCardKey,
  visible: boolean,
): string[] => {
  const unknown = stored.filter((k) => !isKnownKey(k));
  const known = new Set(stored.filter(isKnownKey));

  if (visible) {
    known.add(key);
  } else {
    known.delete(key);
  }

  // Known keys in registry order, then anything this build did not recognise, preserved.
  return [
    ...SUMMARY_CARDS.filter((c) => known.has(c.key)).map((c) => c.key),
    ...unknown,
  ];
};

/** The default band: every card this roster can show. Used by the picker's reset. */
export const defaultCardsFor = (scholarship: Scholarship): string[] =>
  availableCards(scholarship).map((c) => c.key);

/**
 * The roster's stored keys, read at the network boundary.
 *
 * `visibleCards` is non-optional in the type and the API always sends it, but this value
 * arrives as JSON and JSON can lie about its shape. The concrete case: web and API deploy as
 * two separate workflows off the same push, so there is a window where a new web build talks
 * to an API that has no idea what a summary card is. Without this guard every roster detail
 * page throws for the length of that window.
 *
 * A MISSING field falls back to the default band, matching what the API itself does with a
 * null column — the roster has never been configured. It is not confused with a deliberate
 * "show no cards", because that arrives as an empty array rather than nothing at all.
 */
export const storedCards = (scholarship: Scholarship): string[] =>
  Array.isArray(scholarship.visibleCards)
    ? scholarship.visibleCards
    : defaultCardsFor(scholarship);
